import React, { KeyboardEvent, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import CSSModules from 'react-css-modules'
import ReactDOM from 'react-dom'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Element } from 'react-scroll'

import { isAfter, sub } from 'date-fns'
import FocusTrap from 'focus-trap-react'
import { debounce, get, isEmpty, isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { useApptFormattedTime } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/hooks/useApptFormattedTime'
import { PROGRAM_TAXONOMY as PROGRAM_TAXONOMY_CONSTANTS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import {
  BCX_EVENTS,
  COMMON_MIXPANEL_PAGE,
  S4C_EVENTS,
  S4C_PROPERTIES,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'
import { useWindowVisible } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useWindowVisible'
import { parseAllowedMessages } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/messageUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  ChatResponseV2,
  HIGH_ALERT_RISK_LEVEL_V2,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/messagesV2'
import {
  getAiConversationLabels,
  getChatHeaderSubTitleMsg,
  getChatHeaderSubTitleTooltipMsg,
  getChildBadgeLabel,
  hasMultipleAiConversationTypes,
  isAiConversationType,
  resolveGenAiEmergencyNumber,
  resolveGenAiTollFreeNumber,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import {
  hasActiveEpisode,
  hasDirectAccessProvider,
} from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { injectGenAiVariables } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { Feedback, FeedbackEntityType } from '@lyrahealth-inc/shared-app-logic/src/models/feedback/Feedback'
import {
  CONVERSATION_STATE,
  CONVERSATION_TYPE,
  isPatientSideAuthorType,
  LyraLibraryResource,
  MESSAGE_AUTHOR_TYPE,
  MessageAttributes,
} from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { parseEssentialsMetadata } from '@lyrahealth-inc/shared-app-logic/src/models/messages/parseEssentialsMetadata'
import {
  Provider,
  PROVIDER_TYPES,
  ProviderEnums,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import MessageIcon from '@lyrahealth-inc/ui-core/lib/atoms/icons/MessageIcon'
import { HeaderMessagingIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/headerMessagingIcon/HeaderMessagingIcon'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { MessagingSlaDisclaimer } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/messagingSlaDisclaimer/MessagingSlaDisclaimer'
import { Chat } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/chat/Chat'
import {
  MessageThreadList,
  Thread,
} from '@lyrahealth-inc/ui-core-crossplatform/src/pages/messageThreadList/MessageThreadList'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ActivityReviewInfo } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/assignments/Assignments'
import {
  MessageAttachmentData,
  MessageAttachmentType,
  Message as MessageModel,
  MessageType,
} from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/Messages'
import { openUrl } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/expo-linking'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { ThemeScope } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeScope'
import { getAiCoachProvider, tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './messagesPopover.module.scss'
import { useCareNavigatorModal } from '../../../common/hooks/useCareNavigatorModal'
import { useNavigateToEssentialsContent } from '../../../common/hooks/useNavigateToEssentialsContent'
import { useNewMessageSound } from '../../../common/hooks/useNewMessageSound'
import { useSaveMessageDraft } from '../../../common/hooks/useSaveMessageDraft'
import { useShouldShowAICoach } from '../../../common/hooks/useShouldShowAICoach'
import { setTimeInactivityWarning, setTimeLastActive } from '../../../data/auth/authActions'
import {
  getCurrentCountry,
  getCurrentCountryEmergencyNumbersList,
  getCurrentCountryIsoCode,
} from '../../../data/currentCountry/currentCountrySelectors'
import { getCustomerInfo, getCustomerPhone } from '../../../data/customer/customerSelectors'
import {
  getActiveLiveMsgAppt,
  getAwaitingAiResponse,
  getConversationsClient,
  getEpisodes,
  getInLiveMsgSession,
  getLiveMsgProviderId,
  getLiveMsgProviderIdToOpen,
  getMessages as getMessagesSelector,
  getShowMessengerPopover,
  getShownLiveMsgModal,
} from '../../../data/lyraTherapy/lyraTherapySelectors'
import * as MixpanelActions from '../../../data/mixpanel'
import { useAppDispatch } from '../../../data/storeConfiguration/store'
import { putUserProperty } from '../../../data/user/userActions'
import {
  getCareTeamEmail,
  getId,
  getIsUserInternational,
  getIsUserLoggedIn,
  getUserHasConsentedToAiChat,
  getUserHasConsentedToMgcAiChat,
} from '../../../data/user/userSelectors'
import { useGetSelectedProviderConversation } from '../../../hooks/useGetSelectedProviderConversation'
import {
  deleteFeedback,
  deselectMessengerProvider,
  getFeedback,
  getMessage,
  getMessageConversations,
  getMessages,
  incrementUnreadMessageCount,
  saveMessageDraft,
  selectMessengerConversation,
  setAwaitingAiResponse,
  setLiveMsgProviderIdToOpen,
  submitFeedback,
  submitMessage,
  toggleLiveMsgSession,
  toggleMessagesPopover,
  toggleShownLiveMsgModal,
  updateFeedbackResponse,
  updateSelectedConversationId,
  updateUnreadMessageCount,
} from '../data/LyraTherapyActions'
import { getAiCoachClientFeedback, getHasFetchedDashboard } from '../data/LyraTherapySelectors'

import type { ConnectionState } from '../../../data/messenger/twilio-conversations'

const ThreadListTopBar = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  minHeight: 60,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottomWidth: 1,
  borderBottomColor: colors.borderDefault,
  borderStyle: 'solid',
  paddingHorizontal: spacing['20px'],
}))

const ThreadListContainer = styled.View<{ theme: ThemeType; hideList?: boolean; isLargeScreen?: boolean }>(
  ({ theme: { colors }, hideList, isLargeScreen }) => ({
    display: hideList ? 'none' : 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    height: '100%',
    backgroundColor: colors.backgroundPrimary,
    borderRightWidth: 1,
    borderRightColor: colors.borderDefault,
    borderStyle: 'solid',
    width: isLargeScreen ? 240 : '100%',
    flexShrink: 0,
  }),
)

const MessengerContainerStyled = styled.View<{ theme: ThemeType; hide?: boolean }>(({ theme: { colors }, hide }) => ({
  backgroundColor: colors.backgroundPrimary,
  flexGrow: 1,
  maxWidth: 520,
  width: '100%',
  display: hide ? 'none' : 'flex',
}))

const SlaDisclaimerContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing, colors } }) => ({
  borderTopWidth: 1,
  borderTopColor: colors.borderDefault,
  borderStyle: 'solid',
  backgroundColor: colors.backgroundPrimary,
  padding: spacing['16px'],
  flexShrink: 0,
}))

export enum MessageActions {
  ON_TYPING = 'on_typing',
  CLEAR_TYPING = 'clear_typing',
  PROVIDER_SELECTED = 'provider_selected',
  READ_MESSAGE = 'read_message',
  NEW_MESSAGE = 'new_message',
  IS_MOBILE = 'is_mobile',
  POPOVER_CHANGE = 'popover_change',
  SET_INPUT = 'set_input',
  SHOULD_SCROLL_TO_END = 'should_scroll_to_end',
}

export type Action =
  | { type: MessageActions.ON_TYPING; receiverTypingId: string }
  | { type: MessageActions.CLEAR_TYPING; receiverTypingId: string }
  | { type: MessageActions.PROVIDER_SELECTED }
  | { type: MessageActions.READ_MESSAGE }
  | { type: MessageActions.NEW_MESSAGE }
  | { type: MessageActions.IS_MOBILE }
  | { type: MessageActions.POPOVER_CHANGE; isPopoverOpen: boolean }
  | { type: MessageActions.SET_INPUT; inputValue: string }
  | { type: MessageActions.SHOULD_SCROLL_TO_END; scrollToEnd: boolean; animatedScroll: boolean }

type State = {
  isPopoverOpen: boolean
  inputValue: string
  receiverTypingIds: string[]
  isMobile: boolean
  newMessageCount: number
  scrollToEnd: boolean
  animatedScroll: boolean
}

type ConversationsClient = {
  // Twilio object
  on: (eventName: string, callback: (args: any) => void) => void
  off: (eventName: string, callback: (args: any) => void) => void
  getConversationBySid: (selectedConversationId: string) => any
  connectionState: ConnectionState
}

export type MessagesPopoverProps = {
  iconSize?: number
  iconBadgeStyles?: Dict
  iconFilled?: boolean
  isSessionControl?: boolean
  iconFillColor?: string
  customIconStyles?: Dict
  customIconOpenStyles?: Dict
  modalStyle?: Dict
  shouldUseHeaderStyle?: boolean
}

const messageReducer = (state: State, action: Action) => {
  switch (action.type) {
    case MessageActions.ON_TYPING:
      return { ...state, receiverTypingIds: [...state.receiverTypingIds, action.receiverTypingId] }
    case MessageActions.CLEAR_TYPING:
      const newReceiverTypingIds = state.receiverTypingIds.filter((id) => id !== action.receiverTypingId)
      return { ...state, receiverTypingIds: newReceiverTypingIds }
    case MessageActions.PROVIDER_SELECTED:
      return { ...state, newMessageCount: 0 }
    case MessageActions.READ_MESSAGE:
      return { ...state, newMessageCount: 0 }
    case MessageActions.NEW_MESSAGE:
      return { ...state, newMessageCount: state.newMessageCount + 1 }
    case MessageActions.IS_MOBILE:
      return { ...state, isMobile: window.innerWidth < 992 }
    case MessageActions.POPOVER_CHANGE:
      if (action.isPopoverOpen) {
        // open
        return { ...state, isPopoverOpen: action.isPopoverOpen }
      }
      // close
      return { ...state, isPopoverOpen: false, newMessageCount: 0 }
    case MessageActions.SET_INPUT:
      return { ...state, inputValue: action.inputValue }
    case MessageActions.SHOULD_SCROLL_TO_END:
      return { ...state, scrollToEnd: action.scrollToEnd, animatedScroll: action.animatedScroll }
    default:
      throw new Error(`Unhandled action type - ${JSON.stringify(action)}`)
  }
}

const MessagesPopover: React.FC<MessagesPopoverProps> = ({
  iconSize,
  iconBadgeStyles,
  iconFilled = false,
  customIconStyles,
  isSessionControl = false,
  iconFillColor,
  customIconOpenStyles,
  modalStyle,
  shouldUseHeaderStyle,
}) => {
  const AI_TYPING_TIMEOUT = 90000
  const AI_RESPONSE_NAVIGATION_TIMEOUT = 30000
  const aiTypingIndicatorTimeoutId = useRef<ReturnType<typeof setTimeout> | number>()
  const aiTypingIndicatorDelayId = useRef<ReturnType<typeof setTimeout> | number>()
  const aiResponseNavigationTimeoutId = useRef<ReturnType<typeof setTimeout> | number>()
  const intl: IntlShape = useIntl()
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  const [loading, setLoading] = useState(false)
  const messageContainer = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const navigateToEssentials = useNavigateToEssentialsContent()
  const { showAiCoach, showAiCoachConversation } = useShouldShowAICoach()
  const remotePopoverOpen = useSelector(getShowMessengerPopover)
  const awaitingAiResponse = useSelector(getAwaitingAiResponse)
  const userId = useSelector(getId)
  const messagesFromSelector = useSelector(getMessagesSelector)
  const messages = useMemo(() => messagesFromSelector?.toJS(), [messagesFromSelector])
  const conversationsClient = useSelector(getConversationsClient) as ConversationsClient
  const inLiveMsgSession = useSelector(getInLiveMsgSession)
  const activeLiveMsgAppt = useSelector(getActiveLiveMsgAppt)?.toJS()
  const liveMsgProviderId = useSelector(getLiveMsgProviderId)
  const liveMsgProviderIdToOpen = useSelector(getLiveMsgProviderIdToOpen)
  const shownLiveMsgModal = useSelector(getShownLiveMsgModal)
  const contactCarePhoneNumber = useSelector(getCustomerPhone)
  const customerDisplayName = useSelector(getCustomerInfo)
  const emergencyPhoneNumbersList = useSelector(getCurrentCountryEmergencyNumbersList)?.toJS() as string[] | undefined
  const emergencyPhoneNumber = useMemo(
    () => resolveGenAiEmergencyNumber(emergencyPhoneNumbersList),
    [emergencyPhoneNumbersList],
  )
  const currentCountry = useSelector(getCurrentCountry)
  const currentCountryIsoCode = useSelector(getCurrentCountryIsoCode)
  const aiCoachTollFreeNumber = useMemo(() => {
    const list = currentCountry?.get('tollFreeNumbersList')?.toJS() as string[] | undefined
    return resolveGenAiTollFreeNumber(currentCountryIsoCode, list)
  }, [currentCountry, currentCountryIsoCode])
  const aiCoachFeedback = useSelector(getAiCoachClientFeedback)
  const episodesFromSelector = useSelector(getEpisodes)
  const episodes = useMemo(() => episodesFromSelector?.toJS(), [episodesFromSelector])
  const previousRemotePopoverOpen = usePrevious(remotePopoverOpen)
  const userHasConsentedToLccAiChat = useSelector(getUserHasConsentedToAiChat)
  const userHasConsentedToMgcAiChat = useSelector(getUserHasConsentedToMgcAiChat)
  const isUserInternational = useSelector(getIsUserInternational)
  const careTeamEmail = useSelector(getCareTeamEmail)
  const {
    conversations,
    providers,
    selectedConversation,
    selectedConversationId,
    selectedProvider,
    isAICoachSelected,
    selectedDraft,
  } = useGetSelectedProviderConversation()
  const inactiveAICoach = isAICoachSelected && selectedConversation?.conversation_state === CONVERSATION_STATE.INACTIVE
  const { daMessagingSLADisclaimer, isClientDisabled, isTwilioDeprecatedForLyraAI } = useFlags()
  const useV2AiMessaging = isTwilioDeprecatedForLyraAI && isAICoachSelected
  const dashboardFetched = useSelector(getHasFetchedDashboard)
  const isProviderUnavailable =
    dashboardFetched && !isAICoachSelected && !isEmpty(selectedConversation) && !selectedProvider
  const receiverMsgable =
    !isEmpty(selectedConversation) && !inactiveAICoach && !isProviderUnavailable && !selectedProvider?.is_blocked
  const filteredConversations = useMemo(
    () =>
      conversations.filter((conversation) => {
        const isAICoach = isAiConversationType(conversation.conversation_attributes?.type)
        if (isAICoach) {
          if (!showAiCoachConversation) return false
          const state = conversation.conversation_state
          return state === CONVERSATION_STATE.ACTIVE || state === CONVERSATION_STATE.INACTIVE
        }
        return providers.some((p: Provider) => p.provider.lyra_id === conversation.provider_lyra_id)
      }),
    [conversations, providers, showAiCoachConversation],
  )
  const firstConversationId = filteredConversations.length > 0 ? filteredConversations[0].conversation_id : null
  const firstProviderId = filteredConversations.length > 0 ? filteredConversations[0].provider_lyra_id : null
  const { careNavigatorModal, openCareNavigatorModal } = useCareNavigatorModal({
    clickEntryPoint: COMMON_MIXPANEL_PAGE.MESSAGES,
  })
  const [state, messageDispatch] = useReducer(messageReducer, {
    isPopoverOpen: false,
    inputValue: '',
    receiverTypingIds: [],
    isMobile: false,
    newMessageCount: 0,
    scrollToEnd: false,
    animatedScroll: false,
  })

  const { isPopoverOpen, inputValue, receiverTypingIds, isMobile, newMessageCount, scrollToEnd, animatedScroll } = state
  const receiverTyping = receiverTypingIds.some((id) => id === selectedConversationId)
  const selectedProviderLyraId: string | undefined = selectedProvider?.lyra_id
  // memoized save draft with updated inputValue
  const { saveDraft } = useSaveMessageDraft({
    saveMessageDraft: (draft) => dispatch(saveMessageDraft(draft)),
    inputValue,
    selectedConversationId,
  })

  const [playSound] = useNewMessageSound()
  const windowVisible = useWindowVisible()

  const loggedIn = useSelector(getIsUserLoggedIn)

  // tell the conversation that we are typing
  const onTyping: () => Promise<void> = useCallback(async () => {
    if (useV2AiMessaging) return
    if (conversationsClient) {
      const activeConversation = await conversationsClient.getConversationBySid(selectedConversationId)
      if (conversationsClient.connectionState !== 'connected') {
        return
      }
      activeConversation.typing().catch(() => {})
    }
  }, [conversationsClient, selectedConversationId, useV2AiMessaging])

  const onReceiverTyping: (member: {
    conversation: {
      sid: string
    }
    isTyping: boolean
  }) => void = useCallback((member) => {
    const conversationId = member.conversation.sid
    // ensure that the typing started event comes from the selected conversation
    if (member.isTyping) {
      messageDispatch({ type: MessageActions.ON_TYPING, receiverTypingId: conversationId })
    } else {
      messageDispatch({ type: MessageActions.CLEAR_TYPING, receiverTypingId: conversationId })
    }
  }, [])

  const isSelectedConversationAiCoach = useCallback(
    (selectedConversationId: string) => {
      const conversation = conversations.find((c) => c.conversation_id === selectedConversationId)
      return isAiConversationType(conversation?.conversation_attributes.type)
    },
    [conversations],
  )

  const markMessagesAsRead: () => void = useCallback(() => {
    messageDispatch({ type: MessageActions.READ_MESSAGE })
    const isAIConversationSelected = isSelectedConversationAiCoach(selectedConversationId)
    dispatch(
      updateUnreadMessageCount(
        selectedConversationId,
        isAIConversationSelected ? { unread_ai_coach_messages_count: 0 } : { unread_patient_messages_count: 0 },
      ),
    )
  }, [dispatch, selectedConversationId, isSelectedConversationAiCoach])

  const onNewMessage: (message: { conversation: { sid: string }; sid: string }) => void = useCallback(
    (message) => {
      const conversationId = message.conversation.sid
      if (
        isTwilioDeprecatedForLyraAI &&
        get(message, 'state.attributes.author_type') === MESSAGE_AUTHOR_TYPE.AI_COACH
      ) {
        return
      }

      // If message is from self - do nothing
      if (get(message, 'state.attributes.from') === userId) {
        return
      }
      // If the messages have been fetched then add the message
      if (messages?.[conversationId]) {
        ;(dispatch(getMessage({ conversationId, messageId: message.sid })) as any).then(() => {
          // If the conversation is being viewed add an unread message pill
          if (isPopoverOpen && conversationId === selectedConversationId) {
            messageDispatch({ type: MessageActions.NEW_MESSAGE })
          }
        })
      }
      // if popover is not open and window is not visible, when a new message arrives, play a sound
      if (!isPopoverOpen || !windowVisible) {
        playSound()
      }
      // Dismiss typing indicator
      messageDispatch({ type: MessageActions.CLEAR_TYPING, receiverTypingId: conversationId })
      if (get(message, 'state.attributes.author_type') === MESSAGE_AUTHOR_TYPE.AI_COACH) {
        clearTimeout(aiTypingIndicatorTimeoutId.current)
        clearTimeout(aiTypingIndicatorDelayId.current)
        clearTimeout(aiResponseNavigationTimeoutId.current)
        dispatch(setAwaitingAiResponse(false))
      }
      // Always increment counter on new message.
      dispatch(incrementUnreadMessageCount(conversationId))

      // If message is high alert then refetch conversations
      if (get(message, 'state.attributes.metadata.high_alert_risk_level') === 'RISK_LEVEL_RED') {
        dispatch(getMessageConversations(null, userId, isTwilioDeprecatedForLyraAI))
      }
    },
    [
      userId,
      messages,
      isPopoverOpen,
      windowVisible,
      dispatch,
      selectedConversationId,
      playSound,
      isTwilioDeprecatedForLyraAI,
    ],
  )

  const checkForMobile: () => void = () => messageDispatch({ type: MessageActions.IS_MOBILE })

  useEffect(() => {
    checkForMobile()
    window.addEventListener('resize', checkForMobile)
    return () => window.removeEventListener('resize', checkForMobile)
  }, [])

  useEffect(() => {
    if (isMobile && filteredConversations.length > 1) {
      dispatch(deselectMessengerProvider())
    } else if (firstConversationId && firstProviderId) {
      dispatch(selectMessengerConversation(firstConversationId, firstProviderId))
    }
  }, [filteredConversations.length, isMobile, dispatch, firstConversationId, firstProviderId])

  const setInputValue = useCallback(
    (value) => {
      if (selectedProviderLyraId) {
        messageDispatch({ type: MessageActions.SET_INPUT, inputValue: value ?? '' })
        return value
      }
    },
    [selectedProviderLyraId],
  )

  const debouncedSetInputValue = useMemo(() => debounce(setInputValue, 300, { leading: true }), [setInputValue])

  useEffect(() => {
    if (selectedProviderLyraId) {
      debouncedSetInputValue(selectedDraft)
    }
  }, [selectedProviderLyraId, selectedDraft, setInputValue, debouncedSetInputValue])

  const closePopover: () => void = useCallback(() => {
    // user has left the session
    if (inLiveMsgSession) dispatch(toggleLiveMsgSession(false))
    saveDraft()
    messageDispatch({ type: MessageActions.POPOVER_CHANGE, isPopoverOpen: false })
    dispatch(toggleMessagesPopover(false))
    if (isMobile && filteredConversations.length > 1) {
      dispatch(deselectMessengerProvider())
    }
    // If awaiting AI response when navigating away, set timeout to re-enable sending
    if (awaitingAiResponse) {
      aiResponseNavigationTimeoutId.current = setTimeout(() => {
        dispatch(setAwaitingAiResponse(false))
      }, AI_RESPONSE_NAVIGATION_TIMEOUT)
    }
  }, [inLiveMsgSession, saveDraft, isMobile, filteredConversations.length, dispatch, awaitingAiResponse])

  const handleOutsideClick: (e: any) => void = useCallback(
    (e) => {
      if (messageContainer.current && !messageContainer.current.contains(e.target) && isPopoverOpen) {
        closePopover()
      }
    },
    [isPopoverOpen, closePopover],
  )

  useEffect(() => {
    if (conversationsClient) {
      conversationsClient.on('messageAdded', onNewMessage)
      conversationsClient.on('typingStarted', onReceiverTyping)
      conversationsClient.on('typingEnded', onReceiverTyping)
    }
    document.addEventListener('mousedown', handleOutsideClick, false)
    return () => {
      if (conversationsClient) {
        conversationsClient.off('messageAdded', onNewMessage)
        conversationsClient.off('typingStarted', onReceiverTyping)
        conversationsClient.off('typingEnded', onReceiverTyping)
      }
      document.removeEventListener('mousedown', handleOutsideClick, false)
    }
  }, [conversationsClient, onNewMessage, onReceiverTyping, handleOutsideClick])

  const getConversationById = useCallback(
    (conversationId: string) => {
      return conversations.find((conversation) => conversation?.conversation_id === conversationId)
    },
    [conversations],
  )

  const getLatestMessages: (conversationId: string) => void = useCallback(
    (conversationId) => {
      const isAIConversationSelected = isSelectedConversationAiCoach(conversationId)
      setLoading(true)
      ;(dispatch(getMessages(conversationId)) as any)
        .then(() => {
          dispatch(
            updateUnreadMessageCount(
              conversationId,
              isAIConversationSelected ? { unread_ai_coach_messages_count: 0 } : { unread_patient_messages_count: 0 },
            ),
          )
        })
        .finally(() => setLoading(false))
    },
    [dispatch, isSelectedConversationAiCoach],
  )
  const handleAITyping = useCallback(() => {
    // Set typing indicator with slight delay so it looks more natural
    aiTypingIndicatorDelayId.current = setTimeout(
      () => messageDispatch({ type: MessageActions.ON_TYPING, receiverTypingId: selectedConversationId }),
      800,
    )
    /*
     * The typing indicator is cleared when a new message is received.
     * In the rare case that there is an error and a new message is not received we should
     * clear the typing indicator after a set amount of time
     */
    clearTimeout(aiTypingIndicatorTimeoutId.current)
    aiTypingIndicatorTimeoutId.current = setTimeout(
      () => messageDispatch({ type: MessageActions.CLEAR_TYPING, receiverTypingId: selectedConversationId }),
      AI_TYPING_TIMEOUT,
    )
  }, [selectedConversationId])

  const clearV2AiResponseState = useCallback(() => {
    clearTimeout(aiTypingIndicatorTimeoutId.current)
    clearTimeout(aiTypingIndicatorDelayId.current)
    clearTimeout(aiResponseNavigationTimeoutId.current)
    messageDispatch({ type: MessageActions.CLEAR_TYPING, receiverTypingId: selectedConversationId })
    dispatch(setAwaitingAiResponse(false))
  }, [dispatch, selectedConversationId])

  const submitNewMessage: (value: string) => void = useCallback(
    (value) => {
      const submitData = {
        body: value,
        type: 'direct',
        client_id: userId,
      }
      debouncedSetInputValue('')

      // Reset inactivity warning
      dispatch(setTimeInactivityWarning(null))
      dispatch(setTimeLastActive(Date.now()))

      // Disable message sending while waiting for AI response
      if (isAICoachSelected) {
        clearTimeout(aiResponseNavigationTimeoutId.current)
        dispatch(setAwaitingAiResponse(true))
      }

      dispatch(saveMessageDraft({ content: '', conversationId: selectedConversationId }))
      ;(dispatch(submitMessage(selectedConversationId, submitData, useV2AiMessaging)) as any)
        .then(
          (
            response: Partial<Pick<ChatResponseV2, 'high_alert_risk_level'>> & {
              new_conversation_id?: string
            },
          ) => {
            if (useV2AiMessaging) {
              clearV2AiResponseState()
              if (response.high_alert_risk_level === HIGH_ALERT_RISK_LEVEL_V2.RED) {
                dispatch(getMessageConversations(null, userId, isTwilioDeprecatedForLyraAI))
              }
              return
            }
            // Use typing indicator for genAI loading state
            isAICoachSelected && handleAITyping()
            // Update the conversation id if the message is in a previously closed conversation
            if (response?.new_conversation_id) {
              dispatch(
                updateSelectedConversationId({
                  oldConversationId: selectedConversationId,
                  newConversationId: response.new_conversation_id,
                }),
              )
            }
          },
        )
        .catch(() => {
          if (useV2AiMessaging) {
            clearV2AiResponseState()
            return
          }
          // Re-enable sending on error
          if (isAICoachSelected) {
            dispatch(setAwaitingAiResponse(false))
          }
        })

      dispatch(
        MixpanelActions.trackEventWithObj({
          event: BCX_EVENTS.SEND_MESSAGE,
          page: COMMON_MIXPANEL_PAGE.MESSAGES,
        }),
      )
    },
    [
      userId,
      debouncedSetInputValue,
      dispatch,
      selectedConversationId,
      useV2AiMessaging,
      clearV2AiResponseState,
      isTwilioDeprecatedForLyraAI,
      isAICoachSelected,
      handleAITyping,
    ],
  )

  const trackViewMessageConversation = useCallback(
    (conversationType: CONVERSATION_TYPE) => {
      dispatch(
        MixpanelActions.trackEventWithObj({
          event: BCX_EVENTS.VIEW_MESSAGE_CONVERSATION,
          page: COMMON_MIXPANEL_PAGE.MESSAGES,
          action_details: {
            conversationType,
          },
        }),
      )
    },
    [dispatch],
  )

  const openPopover: () => void = useCallback(() => {
    const conversationId = selectedConversation?.conversation_id
    const isSelectedConversationAi = isAiConversationType(selectedConversation?.conversation_attributes?.type)
    // Clear navigation timeout if user comes back before it expires
    clearTimeout(aiResponseNavigationTimeoutId.current)
    if (liveMsgProviderId && !inLiveMsgSession) dispatch(toggleLiveMsgSession(true))
    messageDispatch({ type: MessageActions.POPOVER_CHANGE, isPopoverOpen: true })
    if (showAiCoachConversation && !aiCoachFeedback) {
      dispatch(
        getFeedback({
          lyraId: userId,
          entityType: FeedbackEntityType.AI_COACH,
        }),
      )
    }
    if (!isEmpty(selectedConversation) && conversationId && !messages?.[conversationId]) {
      getLatestMessages(conversationId)
      // TODO: probably need to change tracking here
      dispatch(
        MixpanelActions.trackEventWithObj({
          event: MixpanelActions.MIXPANEL_EVENTS.PAGE_LOAD,
          page: COMMON_MIXPANEL_PAGE.MESSAGES,
        }),
      )
    } else if (
      isSelectedConversationAi &&
      (selectedConversation?.conversation_attributes.unread_ai_coach_messages_count ?? 0) > 0
    ) {
      dispatch(updateUnreadMessageCount(conversationId, { unread_ai_coach_messages_count: 0 }))
    } else if (
      !isSelectedConversationAi &&
      (selectedConversation?.conversation_attributes.unread_patient_messages_count ?? 0) > 0
    ) {
      dispatch(updateUnreadMessageCount(conversationId, { unread_patient_messages_count: 0 }))
    }

    if (!isEmpty(selectedConversation)) {
      const conversationTypeForTracking = isAiConversationType(selectedConversation?.conversation_attributes?.type)
        ? selectedConversation?.conversation_attributes?.type
        : CONVERSATION_TYPE.PROVIDER
      trackViewMessageConversation(conversationTypeForTracking)
    }
  }, [
    selectedConversation,
    liveMsgProviderId,
    inLiveMsgSession,
    dispatch,
    showAiCoachConversation,
    aiCoachFeedback,
    messages,
    userId,
    getLatestMessages,
    trackViewMessageConversation,
  ])

  const onAiCoachFeedbackPressed = useCallback(
    async ({
      value,
      genAiMessageId,
      feedbackResponseId,
    }: {
      value: string | null
      genAiMessageId: string
      feedbackResponseId?: string
    }) => {
      // The feedback buttons disable themselves while this promise is pending
      // (see MessageFeedback), so the await is what keeps them disabled until the
      // request resolves to prevent rapid clicking which causes errors.
      // Errors are hidden entirely since this is fire and forget.
      try {
        // Handle deletion when value is null
        if (value == null) {
          const feedbackItem = aiCoachFeedback?.find(
            (fb: Feedback) => fb?.entity_id === genAiMessageId && fb?.user_type === 'client',
          )
          const feedbackId: string | undefined = feedbackItem?.id ?? feedbackItem?._id
          if (feedbackId) {
            await (dispatch(deleteFeedback(feedbackId, true)) as any)
            await (dispatch(getFeedback({ lyraId: userId, entityType: FeedbackEntityType.AI_COACH })) as any)
          }
          return
        }

        // Handle update if feedbackResponseId exists
        if (feedbackResponseId) {
          await (dispatch(updateFeedbackResponse({ feedbackResponseId, answer: value }, true)) as any)
        } else {
          // Handle creation of new feedback
          await (dispatch(
            submitFeedback(
              {
                entity_type: FeedbackEntityType.AI_COACH,
                entity_id: genAiMessageId,
                lyra_id: userId,
                user_type: 'client',
                feedback_response: [{ question: 'ai_coach_response_thumbs_feedback', answer: value }],
              },
              true,
            ),
          ) as any)
        }
      } catch (error) {
        console.error('AI Coach feedback request failed', error)
      }
    },
    [dispatch, userId, aiCoachFeedback],
  )

  // toggles popover from state
  useEffect(() => {
    if (previousRemotePopoverOpen !== remotePopoverOpen) {
      remotePopoverOpen ? openPopover() : closePopover()
    }
  }, [closePopover, openPopover, previousRemotePopoverOpen, remotePopoverOpen])

  const togglePopover: () => void = useCallback(() => {
    if (isSessionControl) {
      dispatch(
        MixpanelActions.trackEventWithObj({
          page: COMMON_MIXPANEL_PAGE.VIDEO_SESSION,
          event: MixpanelActions.MIXPANEL_EVENTS.BUTTON_PRESS,
          action: MixpanelActions.MIXPANEL_ACTION.TOGGLE_MESSENGER,
          details: MixpanelActions.MIXPANEL_USER_TYPE.CLIENT,
        }),
      )
    }

    if (!isPopoverOpen) {
      if (!shownLiveMsgModal && liveMsgProviderId) {
        dispatch(
          MixpanelActions.trackEventWithObj({
            page: COMMON_MIXPANEL_PAGE.MESSAGES,
            event: MixpanelActions.MIXPANEL_EVENTS.LIVE_MESSAGE_PRIMER,
          }),
        )
        dispatch(toggleShownLiveMsgModal(true))
      } else {
        openPopover()
      }
    } else {
      closePopover()
    }
  }, [isSessionControl, isPopoverOpen, dispatch, shownLiveMsgModal, liveMsgProviderId, openPopover, closePopover])

  const onChatBubblePressed = useCallback(
    (_messageId: string, _receiver: boolean, activityInfo?: ActivityReviewInfo) => {
      switch (activityInfo?.activityType) {
        case 'feedback':
          dispatch(
            MixpanelActions.trackEventWithObj({
              event: MixpanelActions.MIXPANEL_EVENTS.BUTTON_PRESS,
              page: COMMON_MIXPANEL_PAGE.MESSAGES,
              action: MixpanelActions.MIXPANEL_ACTION.OPEN_ACTIVITY_ENTRY,
            }),
          )
          navigate(`/secure/assignments/${activityInfo.id}`, {
            state: { responseId: activityInfo.responseId },
          })
          break
        case 'session_cancellation':
        case 'request_session_time':
          navigate('/secure/index/sessions')
          break
        default:
          dispatch(
            MixpanelActions.trackEventWithObj({
              event: MixpanelActions.MIXPANEL_EVENTS.BUTTON_PRESS,
              page: COMMON_MIXPANEL_PAGE.MESSAGES,
              action: MixpanelActions.MIXPANEL_ACTION.OPEN_MESSAGE_URL,
            }),
          )
      }
      closePopover()
    },
    [closePopover, dispatch, navigate],
  )

  const selectConversation: (conversationId: string) => void = useCallback(
    (conversationId) => {
      const conversation = getConversationById(conversationId)
      const providerId = conversation?.provider_lyra_id
      if (!conversation || !providerId) return
      const conversationType = conversation.conversation_attributes?.type
      const isAiConversation = isAiConversationType(conversationType)
      // save drafts when switching between conversations
      const input = debouncedSetInputValue.flush()
      dispatch(saveMessageDraft({ content: input, conversationId: selectedConversationId }))
      if (inLiveMsgSession) dispatch(toggleLiveMsgSession(liveMsgProviderId === providerId))
      messageDispatch({ type: MessageActions.PROVIDER_SELECTED })
      dispatch(selectMessengerConversation(conversationId, providerId))
      if (!messages?.[conversationId]) {
        getLatestMessages(conversationId)
      } else if (isAiConversation && (conversation.conversation_attributes.unread_ai_coach_messages_count ?? 0) > 0) {
        dispatch(updateUnreadMessageCount(conversationId, { unread_ai_coach_messages_count: 0 }))
      } else if (!isAiConversation && (conversation.conversation_attributes.unread_patient_messages_count ?? 0) > 0) {
        dispatch(updateUnreadMessageCount(conversationId, { unread_patient_messages_count: 0 }))
      }

      const conversationTypeForTracking = isAiConversationType(conversationType)
        ? conversationType
        : CONVERSATION_TYPE.PROVIDER

      trackViewMessageConversation(conversationTypeForTracking)
    },
    [
      getConversationById,
      debouncedSetInputValue,
      dispatch,
      selectedConversationId,
      inLiveMsgSession,
      liveMsgProviderId,
      messages,
      getLatestMessages,
      trackViewMessageConversation,
    ],
  )

  // effect to start live msg session from banner
  useEffect(() => {
    if (liveMsgProviderIdToOpen) {
      const conversation = conversations.find((c) => c.provider_lyra_id === liveMsgProviderIdToOpen)
      if (conversation) {
        openPopover()
        selectConversation(conversation.conversation_id)
      }
      dispatch(setLiveMsgProviderIdToOpen(null))
    }
  }, [liveMsgProviderIdToOpen, openPopover, selectConversation, dispatch, conversations])

  const hasDAProvider = useMemo(() => hasDirectAccessProvider({ providers }), [providers])

  const hasMultipleAiTypes = hasMultipleAiConversationTypes(filteredConversations)

  const messageThreads = useMemo((): Thread[] => {
    const appointments = providers.flatMap((p: Provider) => p.appointments ?? [])
    return filteredConversations.map((conversation) => {
      const conversationType = conversation.conversation_attributes?.type
      const isAICoach = isAiConversationType(conversationType)
      const aiConversationLabels = getAiConversationLabels(conversationType, hasMultipleAiTypes)
      const provider: Provider | undefined = isAICoach
        ? getAiCoachProvider({ providerLyraId: conversation.provider_lyra_id })
        : providers.find((p: Provider) => p.provider.lyra_id === conversation.provider_lyra_id)
      const providerInfo = provider?.provider
      const unreadMsgCount =
        (conversation.conversation_attributes.unread_patient_messages_count ?? 0) +
        (conversation.conversation_attributes.unread_ai_coach_messages_count ?? 0)
      return {
        conversationId: conversation.conversation_id,
        avatarDetails: {
          src: providerInfo?.lyraProviderImage?.imageUrl ?? '',
          displayName: providerInfo?.display_name ?? '',
        },
        threadTitleMsg: isAICoach ? aiConversationLabels.threadTitle : (providerInfo?.display_name ?? ''),
        threadSubTitleMsg: isAICoach
          ? aiConversationLabels.threadSubtitle
          : (PROVIDER_TYPES[ProviderEnums[providerInfo?.lyra_type?.toUpperCase() as keyof typeof ProviderEnums]]
              ?.TITLE ?? ''),
        threadMsg: '',
        timeStampMsg: conversation.conversation_last_message_sent_datetime ?? '',
        timeZone: '',
        unreadMsgCount,
        isAICoach,
        childBadgeLabel: getChildBadgeLabel(intl, appointments, conversation.patient_lyra_id, userId ?? ''),
      }
    })
  }, [filteredConversations, providers, intl, userId, hasMultipleAiTypes])

  const renderThreadList = (): JSX.Element | void => {
    const hideList: boolean = isMobile && !isEmpty(selectedProvider)
    if (filteredConversations.length > 1) {
      return (
        <ThreadListContainer hideList={hideList} isLargeScreen={!isMobile} testID={tID('MessagesPopover-threadList')}>
          {!isMobile ? null : (
            <ThreadListTopBar>
              <Title
                size={TitleSize.MEDIUM}
                text={<FormattedMessage defaultMessage='Messages' description='title' />}
              />
              <PressableOpacity
                onPress={closePopover}
                testID={tID('Messenger-close')}
                accessibilityLabel={intl.formatMessage({
                  defaultMessage: 'Close messenger',
                  description: 'Accessibility label for close messenger button',
                })}
              >
                <XIcon fillColor={colors.iconDefault} />
              </PressableOpacity>
            </ThreadListTopBar>
          )}
          <MessageThreadList
            messageThreads={messageThreads}
            onThreadPressed={selectConversation}
            isSidebarView
            selectedConversationId={selectedConversationId}
          />
          {daMessagingSLADisclaimer && hasDAProvider && (
            <SlaDisclaimerContainer testID={tID('MessagesPopover-providerNotice')}>
              <MessagingSlaDisclaimer
                contactCarePhoneNumber={contactCarePhoneNumber || ''}
                emergencyPhoneNumber={emergencyPhoneNumber || ''}
              />
            </SlaDisclaimerContainer>
          )}
        </ThreadListContainer>
      )
    }
  }

  const selectedMessages = useMemo(
    () => messages?.[selectedConversationId]?.conversation_messages,
    [messages, selectedConversationId],
  )
  const getMessageType = (attributes: MessageAttributes) => {
    const linkText = attributes.metadata?.linkText
    const activityResponseId = attributes.metadata?.activity_response_id
    const activityId = attributes.metadata?.activity_id
    const isDeletedMessage =
      !isNil(attributes?.retention_status) && attributes?.retention_status === 'DELETED_LIVE_MESSAGE'
    const isActivity = Boolean(linkText) || (Boolean(activityResponseId) && Boolean(activityId))

    if (isDeletedMessage) {
      return MessageType.DELETED
    } else if (isActivity) {
      return MessageType.ACTIVITY
    } else {
      return MessageType.CHAT_BUBBLE
    }
  }

  const data = useMemo(() => {
    if (isEmpty(selectedMessages)) return []
    const msgs = parseAllowedMessages(selectedMessages).map((m, index) => {
      const attributes = JSON.parse(m.attributes)
      const essentials = attributes.metadata?.essentials
      const attachedResources = attributes.metadata?.attached_resources as LyraLibraryResource[] | undefined
      const parsedEssentials = parseEssentialsMetadata(attachedResources, essentials)
      const genAiMessageId = attributes.metadata?.gen_ai_message_id
      const isPatient = isPatientSideAuthorType(attributes.author_type)
      const previousMessage = selectedMessages[index - 1]
      let showTime = true
      const dateCreated = new Date(m.date_created)
      // we only want to show the time stamp if 10 minutes have passed
      showTime = isAfter(sub(dateCreated, { minutes: 10 }), new Date(previousMessage?.date_created))
      const messageId = m.message_id
      const messageFeedback = aiCoachFeedback?.find((feedback: Feedback) => feedback.entity_id === genAiMessageId)
      const messageFeedbackValue = messageFeedback?.feedback_response?.[0]?.answer
      const feedbackResponseId = messageFeedback?.feedback_response?.[0]?.id
      const riskLevel = attributes.metadata?.risk_level
      const messageBody =
        attributes.author_type === MESSAGE_AUTHOR_TYPE.AI_COACH
          ? (injectGenAiVariables(m.body, {
              employer: customerDisplayName,
              toll_free_number: aiCoachTollFreeNumber,
              emergency_phone_number: emergencyPhoneNumber,
            }) ?? m.body)
          : m.body
      return {
        messageId,
        receiver: !isPatient,
        dateCreated: m?.date_created,
        message: messageBody,
        isLatestMessage: index === selectedMessages.length - 1,
        // if the message has no status, it has been sent
        sendStatus: m.sendStatus === undefined ? 'sent' : m.sendStatus,
        showTime,
        linkText: attributes.metadata?.linkText,
        activityResponseId: attributes.metadata?.activity_response_id,
        activityId: attributes.metadata?.activity_id,
        submitDate: attributes.metadata?.submit_date,
        activityTitle: attributes.metadata?.title,
        type: getMessageType(attributes),
        messageType: attributes.message_type,
        attachments: attributes.metadata?.attachments,
        avatarDetails:
          !isPatient && selectedProvider
            ? {
                src: selectedProvider?.lyraProviderImage?.imageUrl,
                displayName: selectedProvider?.display_name,
              }
            : undefined,
        essentials: parsedEssentials,
        messageFeedbackValue,
        genAiMessageId,
        feedbackResponseId,
        riskLevel,
      }
    })
    return msgs.reverse()
  }, [
    aiCoachFeedback,
    aiCoachTollFreeNumber,
    customerDisplayName,
    emergencyPhoneNumber,
    selectedMessages,
    selectedProvider,
  ])

  const { sessionTime } = useApptFormattedTime(activeLiveMsgAppt)

  const hideMessenger: boolean = isMobile && !selectedConversationId
  // add all unread messages together
  let unreadMessageCount = 0
  filteredConversations.forEach((conversation) => {
    unreadMessageCount +=
      (conversation.conversation_attributes.unread_patient_messages_count ?? 0) +
      (conversation.conversation_attributes.unread_ai_coach_messages_count ?? 0)
  })
  const selectedActiveLiveMsgApt: boolean = liveMsgProviderId === selectedProvider?.lyra_id

  const onAttachmentPressed = useCallback(
    (attachment: MessageAttachmentData, item: MessageModel) => {
      switch (attachment.type) {
        case MessageAttachmentType.DBL:
          if (!selectedProviderLyraId) {
            return
          }

          const activeEpisode = hasActiveEpisode({ episodes, providerId: selectedProviderLyraId })
          if (activeEpisode) {
            navigate(`/secure/index/provider/${selectedProviderLyraId}/rebook`)
          } else {
            navigate(`/secure/index/provider/${selectedProviderLyraId}?limit=${btoa(item.dateCreated)}`)
          }
          togglePopover()
          return
        case MessageAttachmentType.ESSENTIALS_LINK:
          if (attachment?.url) {
            navigate(`/secure/index/essentials${attachment.url}`)
          }
          return
      }
    },
    [episodes, navigate, selectedProviderLyraId, togglePopover],
  )

  const onEssentialsPress = useCallback(
    ({ url, title, contentType }: { url: string; title: string; contentType: string }) => {
      dispatch(
        MixpanelActions.trackEventWithObj({
          event: MixpanelActions.MIXPANEL_EVENTS.BUTTON_PRESS,
          page: COMMON_MIXPANEL_PAGE.MESSAGES,
          action: BCX_EVENTS.CLICK_AI_COACH_ESSENTIALS_CARD,
          content_title: title,
          content_type: contentType,
          action_details: {
            conversationType: selectedConversation?.conversation_attributes?.type,
          },
        }),
      )
      navigateToEssentials({ subPath: url, loggedIn, referrer: 'AI Coach' })
    },
    [dispatch, navigateToEssentials, loggedIn, selectedConversation?.conversation_attributes?.type],
  )

  const setInputValueAndStartTypingIndicator = useCallback(
    (value: string | undefined) => {
      onTyping()
      debouncedSetInputValue(value)
    },
    [onTyping, debouncedSetInputValue],
  )

  const onNewMsgPressed = useCallback(
    (dismissNewMsgIndicator = false) => {
      messageDispatch({
        type: MessageActions.SHOULD_SCROLL_TO_END,
        scrollToEnd: true,
        animatedScroll: dismissNewMsgIndicator,
      })
      markMessagesAsRead()
    },
    [markMessagesAsRead],
  )

  // reset the scroll after scrolling
  const onScrollComplete = useCallback(() => {
    messageDispatch({
      type: MessageActions.SHOULD_SCROLL_TO_END,
      scrollToEnd: false,
      animatedScroll: false,
    })
  }, [])

  const chat = useMemo(() => {
    if (isEmpty(selectedProvider) && !isProviderUnavailable) {
      return (
        <div className='none-selected'>
          <FormattedMessage
            defaultMessage='Please select a provider to message'
            description='prompt user to select a provider they would like to message'
          />
        </div>
      )
    }

    const onLinkPress = (link: string) => {
      // is it a direct Program Booking Link
      if (link.indexOf('onboard/match-location?directPath=true')) {
        dispatch(
          MixpanelActions.trackEventWithObj({
            event: `${S4C_EVENTS.CLICK_PROGRAM_BOOKING_LINK}`,
            referring_provider_id: selectedProvider?.lyra_id,
            is_direct_path: true,
            [S4C_PROPERTIES.DIRECT_LINK_SOURCE]: 'provider_message',
          }),
        )
      }
    }

    const isProviderDA =
      daMessagingSLADisclaimer &&
      selectedProvider?.program_taxonomy?.some((pt) => pt?.partner === PROGRAM_TAXONOMY_CONSTANTS.partner.directAccess)

    const onContactCareNavigatorPressed = () => {
      closePopover()
      openCareNavigatorModal()
    }
    const aiConversationLabels = getAiConversationLabels(
      selectedConversation?.conversation_attributes?.type,
      hasMultipleAiTypes,
    )
    const conversationType = selectedConversation?.conversation_attributes?.type
    const isManagerCoachingAi = conversationType === CONVERSATION_TYPE.MANAGER_COACHING_AI
    const hasConsentedToSelectedAiChat = isManagerCoachingAi ? userHasConsentedToMgcAiChat : userHasConsentedToLccAiChat
    const consentUserPropertyName = isManagerCoachingAi ? 'hasConsentedToMGCAIChat' : 'hasConsentedToAIChat'
    return (
      <Chat
        displayName={selectedProvider?.display_name ?? ''}
        avatarImage={selectedProvider?.lyraProviderImage?.imageUrl}
        chatHeaderTitle={isAICoachSelected ? aiConversationLabels.chatTitle : (selectedProvider?.display_name ?? '')}
        chatHeaderSubTitleTooltipMsg={getChatHeaderSubTitleTooltipMsg(
          isAICoachSelected,
          selectedConversation?.conversation_attributes?.type,
        )}
        chatHeaderSubTitleMsg={getChatHeaderSubTitleMsg({
          countryEmergencyNumbers: [emergencyPhoneNumber],
          contactCarePhoneNumber,
          providerName: selectedProvider?.display_name,
          isProviderBlocked: selectedProvider?.is_blocked ?? false,
          isProviderDA,
          hasActiveAppointment: selectedActiveLiveMsgApt && !isEmpty(activeLiveMsgAppt),
          sessionTime,
          inVideoSession: false,
          isGenAI: isAICoachSelected,
          conversationType: selectedConversation?.conversation_attributes?.type,
          renderLink: (phone: string) => (
            <Link size={BodyTextSize.SMALL} text={phone} onPress={() => openUrl(`tel:${phone}`)} />
          ),
          renderContactLyraLink: () => (
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            <Link size={BodyTextSize.SMALL} text={'contact Lyra'} onPress={onContactCareNavigatorPressed} />
          ),
        })}
        liveSessionBadge={selectedActiveLiveMsgApt && !isEmpty(activeLiveMsgAppt) ? 'LIVE SESSION' : ''}
        messages={data}
        onBack={isMobile ? () => dispatch(deselectMessengerProvider()) : closePopover}
        onSendMessage={submitNewMessage}
        onInputChange={setInputValueAndStartTypingIndicator}
        receiverTyping={receiverTyping}
        contactCarePhoneNumber={contactCarePhoneNumber}
        careTeamEmail={careTeamEmail}
        receiverMsgable={receiverMsgable}
        receiverBlocked={selectedProvider?.is_blocked}
        isLoading={loading}
        showNewMsgIndicator={newMessageCount > 0}
        unreadMsgCount={newMessageCount}
        inputValue={inputValue}
        showChevron
        showClose={isMobile && filteredConversations.length > 1}
        shouldInvert={data.length > 0}
        onChatBubblePressed={onChatBubblePressed}
        onLinkPress={onLinkPress}
        onChevronPressed={closePopover}
        conversationDateCreated={selectedConversation?.conversation_date_created}
        onContactCareNavigatorPressed={onContactCareNavigatorPressed}
        onAiCoachCareNavigatorPressed={onContactCareNavigatorPressed}
        onAttachmentPressed={(attachment, item) => onAttachmentPressed(attachment, item)}
        sendOnEnter
        scrollInfo={{ scrollToEnd, animatedScroll }}
        onNewMsgPressed={onNewMsgPressed}
        onScrollComplete={onScrollComplete}
        onEssentialsPress={onEssentialsPress}
        isAiCoachSelected={isAICoachSelected}
        conversationType={conversationType}
        onAiCoachFeedbackPressed={onAiCoachFeedbackPressed}
        showAiChatConsent={!inactiveAICoach}
        hasConsentedToAiChat={hasConsentedToSelectedAiChat}
        isInternational={isUserInternational}
        onConsentToAiChat={(enable) => {
          return dispatch(
            putUserProperty({
              lyraId: userId,
              requestData: { name: consentUserPropertyName, value: String(enable), classType: 'Boolean' },
            }),
          )
        }}
        // PROVREC-5203 Disable being able to send messages if the client is disabled
        // CPR-2178 Disable sending while waiting for AI response
        disableSend={isClientDisabled || (isAICoachSelected && awaitingAiResponse)}
        hideTimestampOnHover
        showHeaderDivider={!isAuroraTheme}
        dblAttachmentsDisabled={selectedProvider?.is_blocked}
        showBlockedChatView={isProviderUnavailable}
      />
    )
  }, [
    selectedProvider,
    daMessagingSLADisclaimer,
    emergencyPhoneNumber,
    contactCarePhoneNumber,
    selectedActiveLiveMsgApt,
    activeLiveMsgAppt,
    sessionTime,
    isAICoachSelected,
    data,
    isMobile,
    closePopover,
    submitNewMessage,
    setInputValueAndStartTypingIndicator,
    receiverTyping,
    careTeamEmail,
    receiverMsgable,
    loading,
    newMessageCount,
    inputValue,
    filteredConversations.length,
    onChatBubblePressed,
    selectedConversation?.conversation_date_created,
    selectedConversation?.conversation_attributes?.type,
    hasMultipleAiTypes,
    scrollToEnd,
    animatedScroll,
    onNewMsgPressed,
    onScrollComplete,
    onEssentialsPress,
    onAiCoachFeedbackPressed,
    inactiveAICoach,
    userHasConsentedToLccAiChat,
    userHasConsentedToMgcAiChat,
    isUserInternational,
    isClientDisabled,
    awaitingAiResponse,
    dispatch,
    openCareNavigatorModal,
    onAttachmentPressed,
    userId,
    isAuroraTheme,
    isProviderUnavailable,
  ])

  const withPortal = (component: React.ReactNode) => {
    // return component
    // Match mobileonly breakpoint. useTheme breakpoints.isMobileSized is not consistent with the old scss breakpoints
    return window.innerWidth <= 768 ? ReactDOM.createPortal(component, document.body) : component
  }

  const iconStyles = isPopoverOpen
    ? {
        ...customIconStyles,
        ...customIconOpenStyles,
      }
    : customIconStyles

  return (
    <div data-test-id='MessagesPopover'>
      <Element name='' id='lyraweb-lyraTherapy-messagesScrollTo'>
        {shouldUseHeaderStyle ? (
          <HeaderMessagingIcon
            showGenAi={showAiCoach && unreadMessageCount === 0}
            showUnread={unreadMessageCount > 0}
            onPress={togglePopover}
            isExpanded={isPopoverOpen}
          />
        ) : (
          <MessageIcon
            styleName={`message-icon ${shouldUseHeaderStyle ? 'header-messages-icon' : ''} ${
              isSessionControl ? 'session-control' : ''
            }`}
            style={iconStyles}
            className='message-icon'
            customBadgeStyle={iconBadgeStyles}
            width={iconSize}
            unreadMessages={isPopoverOpen ? 0 : unreadMessageCount}
            onClick={togglePopover}
            isFilled={iconFilled}
            data-test-id='MessagesPopover-MessageIcon'
            fillColor={iconFillColor ? iconFillColor : isPopoverOpen ? styles.x_primary_action : styles.x_soft_black}
            // @ts-expect-error TS(2322): Type '{ styleName: string; style: Dict | undefined... Remove this comment to see the full error message
            role='button'
            aria-label={intl.formatMessage({
              defaultMessage: 'Toggle messenger',
              description: 'Either to close or open the messenger',
            })}
            aria-expanded={isPopoverOpen}
            tabIndex={0}
            withBadge
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                togglePopover()
              }
            }}
          />
        )}
      </Element>
      {withPortal(
        <>
          {isPopoverOpen ? <div className='messages-popover-backdrop' /> : null}
          <div
            className={`lt-messages-popover-container messages-popover-modal ${
              filteredConversations.length > 1 ? 'large' : ''
            } ${liveMsgProviderId ? 'session-banner' : ''}`}
            ref={messageContainer}
          >
            {isPopoverOpen ? (
              <ThemeScope forceDarkMode>
                <FocusTrap focusTrapOptions={{ fallbackFocus: '#lyraweb-lyraTherapy-messagesScrollTo' }}>
                  <div
                    className='messages-popover-dialog'
                    aria-modal
                    aria-label={intl.formatMessage({
                      defaultMessage: 'Provider Messages',
                      description: 'Pop up that contains provider messages',
                    })}
                    role='dialog'
                    style={modalStyle}
                  >
                    {!dashboardFetched ? (
                      <LoadingIndicator />
                    ) : (
                      <>
                        {renderThreadList()}
                        <MessengerContainerStyled
                          hide={hideMessenger}
                          testID={tID('MessagesPopover-messengerContainer')}
                        >
                          {chat}
                        </MessengerContainerStyled>
                      </>
                    )}
                  </div>
                </FocusTrap>
              </ThemeScope>
            ) : null}
          </div>
        </>,
      )}
      {careNavigatorModal}
    </div>
  )
}

export default CSSModules(MessagesPopover, styles, { allowMultiple: true })
